/**
 * A photo, made into an avatar — owner, 2026-09-13: 使用者上傳自己的頭像.
 *
 * Resized HERE, in the browser, before anything is uploaded. A phone camera hands
 * over three to eight megabytes; an avatar is drawn at 36 to 56 CSS pixels. Sending
 * the original would spend a person's data on detail no screen in this app can show,
 * and the server would have to refuse it (the store caps what it will keep).
 *
 * SQUARE, CENTRE-CROPPED, because every frame that draws one is a circle: a portrait
 * photo squeezed into a circle is a face with its ears cut off, while a centre crop
 * of the short side is what a person expects from every other app that does this.
 *
 * WebP where the browser can encode it (about half the bytes at the same quality),
 * JPEG otherwise. Not PNG: a photograph in PNG is several times the size for no gain
 * — though the store still accepts one, because a paste from a screenshot is a PNG
 * and refusing it at the door would be rude.
 */

/** What the frames actually draw, doubled for retina. Bigger is bytes nobody sees. */
const AVATAR_PX = 256

/** The server's own ceiling (bff profile.py MAX_AVATAR_BYTES), stated once here so
 *  the quality ladder below has something to aim under. Not exported: the module's
 *  job is to return something that fits, not to publish the number it aims at. */
const MAX_UPLOAD_BYTES = 128 * 1024

import { withBlobUrl } from '../ui/blobUrl'

export type AvatarBytes = { data: string; mime: string }

/** Quality steps, tried in order until the result fits. The last one always does at
 *  this size — a 256px photo at q0.5 is a few kilobytes. */
const QUALITY = [0.85, 0.7, 0.55]

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ''
  // In chunks: String.fromCharCode(...bytes) on a 100KB array overflows the argument
  // limit and throws — a crash on exactly the large pictures this exists to handle.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(s)
}

/**
 * Draw `file` into a square canvas and encode it small enough to send.
 *
 * Throws with a sentence a person can act on: the failures here are "that is not an
 * image this browser can read" and "this browser cannot encode", both of which are
 * about the file they just picked.
 */
export async function avatarFromFile(file: Blob, px = AVATAR_PX): Promise<AvatarBytes> {
  return pictureFromFile(file, px, px, MAX_UPLOAD_BYTES)
}

/** A banner as drawn across the top of a profile page — 3:1, doubled for retina. */
const BANNER_W = 1200
const BANNER_H = 400
/** Under the server's MAX_BANNER_BYTES (bff profile.py), with the same headroom as the face. */
const MAX_BANNER_UPLOAD_BYTES = 230 * 1024

/**
 * THE BANNER — owner, 2026-10-20: 個人頁面可以設定頭像以及 banner. The face's path at another shape:
 * the CENTRE 3:1 strip of the photo, so a portrait gives its middle band and a panorama its middle
 * stretch — what the page will show is exactly what is sent.
 */
export async function bannerFromFile(file: Blob): Promise<AvatarBytes> {
  return pictureFromFile(file, BANNER_W, BANNER_H, MAX_BANNER_UPLOAD_BYTES)
}

async function pictureFromFile(file: Blob, w: number, h: number, cap: number): Promise<AvatarBytes> {
  const bitmap = await loadBitmap(file)
  // The largest w:h window that fits the source, centred on it. For a square that is the short side.
  const wide = bitmap.width / bitmap.height > w / h
  const sw = wide ? bitmap.height * (w / h) : bitmap.width
  const sh = wide ? bitmap.height : bitmap.width * (h / w)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot prepare the image.')
  // The centre window of the source, scaled to fill — one drawImage, no intermediate.
  ctx.drawImage(
    bitmap as CanvasImageSource,
    (bitmap.width - sw) / 2,
    (bitmap.height - sh) / 2,
    sw,
    sh,
    0,
    0,
    w,
    h,
  )
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close()

  for (const mime of ['image/webp', 'image/jpeg']) {
    for (const q of QUALITY) {
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, mime, q))
      // A browser that cannot encode this type hands back a PNG (or null); taking its
      // word for the type would upload PNG bytes labelled as WebP.
      if (!blob || blob.type !== mime) break
      if (blob.size <= cap) return { data: toBase64(await blob.arrayBuffer()), mime }
    }
  }
  throw new Error('Could not shrink that image — try a different one.')
}

/**
 * The picture as something drawable. `createImageBitmap` where it exists (it decodes
 * off the main thread and handles EXIF orientation), an <img> otherwise — Safari
 * carried no bitmap support for years and is the platform this app is used on most.
 */
async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file)
    } catch {
      /* not a format this browser decodes that way; fall through to the element */
    }
  }
  // Through withBlobUrl, the app's one hand-it-over-then-release helper: it owns the
  // revoke, on the grace the rest of the app uses. A mint and a timer written here
  // would be a fourth lifecycle to get wrong — and both guards (blobUrl, noHeartbeat)
  // refuse one, which is how this line came to be written the right way round.
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    withBlobUrl(file, (url) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('That file is not an image this browser can read.'))
      img.src = url
    })
  })
}
